/**
 * How charges appear on the card statement (TMMAPP-0119 item 12): the served
 * `GET /v1/me/billing/client-config` `statementDescriptor`, never a hard-coded value. Null while
 * loading, when not served, and when the config can't be read: the line is then omitted.
 *
 * The same read carries the customer emails Stripe sends itself (`stripeCustomerEmails`,
 * TMMAPP-0124 item 8): empty until read, and when none is served.
 */
import { useEffect, useState } from 'react';
import { billingClientConfigApi, type StripeCustomerEmail } from '@/api/billingClientConfig';

export interface ClientConfigDisplay {
  statementDescriptor: string | null;
  customerEmails: StripeCustomerEmail[];
}

const NOTHING: ClientConfigDisplay = { statementDescriptor: null, customerEmails: [] };

export function useClientConfigDisplay(enabled: boolean): ClientConfigDisplay {
  const [display, setDisplay] = useState<ClientConfigDisplay>(NOTHING);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    billingClientConfigApi
      .get()
      .then((result) => {
        if (cancelled) return;
        setDisplay(
          result.kind === 'stripe'
            ? {
                statementDescriptor: result.config.statementDescriptor,
                customerEmails: result.config.customerEmails,
              }
            : NOTHING,
        );
      })
      .catch(() => {
        /* the lines are omitted */
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return enabled ? display : NOTHING;
}
